import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { confirmarNroDespacho } from '../api';

/**
 * Two-button yes/no confirmation for flipping
 * `NroDespachoConfirmado_RO='SI'` on the venta header. Runs as a
 * post-only action; doesn't interact with the Confirmar Despacho form.
 */
export function ConfirmarNroDespachoDialog({
  ventaId,
  onClose,
  onConfirmed,
}: {
  ventaId: string;
  onClose: () => void;
  onConfirmed: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await confirmarNroDespacho(ventaId);
      if (res.outcome === 'confirmed') {
        toast.success('N° de despacho confirmado');
        onConfirmed();
      } else if (res.outcome === 'already_confirmed') {
        toast.info('El N° de despacho ya estaba confirmado');
        onConfirmed();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('No se pudo confirmar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={busy && 'Confirmando despacho…'}
      >
        <DialogHeader>
          <DialogTitle>Confirmar N° de Despacho</DialogTitle>
          <DialogDescription>
            ¿Estas seguro de confirmar el número de despacho de esta venta?
          </DialogDescription>
        </DialogHeader>
        <DialogBody />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className="bg-foreground text-background hover:bg-foreground/90"
          >
            Aprobar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
